import { useRef, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Button,
    Alert,
    Divider
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useDispatch, useSelector } from 'react-redux';
import EquipmentList from './equipment/EquipmentList';
import { getEquipment, postEquipment } from '../../../../../../actions/rof-burns/rof-actions';

// Must match EQUIPMENT_TYPES in ikhala-core base/views.py
const equipmentTypes = [
    'Tractor',
    'Loader (Front-end, Wheel, Skid steer and TLB/Backhoe)',
    'Excavator',
    'Logger / Forestry Machine (Skidder, Forwarder, Log Loader)',
    'Bulldozer',
    'Tele-handler',
    'Truck (Tipper, Loader Crane)',
    'Other',
];

// Must match EQUIPMENT_MASS_UNITS in ikhala-core base/views.py
const massUnits = [
    'Kilogram',
    'Metric tonne',
    'Pound',
    'US ton',
];

const initialState = {
    name: '',
    type: '',
    typeOther: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    mass: '',
    massUnit: '',
    sourceDocument: null,
};

export default function Equipment() {

    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.postFormData);
    let { id } = selectedProject || {}
    const projectKey = id
    const fileInput = useRef(null);
    const [formError, setFormError] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [equipmentDetails, setEquipmentDetails] = useState({ ...initialState })

    const setData = e => {
        const { name, value } = e.target;
        setEquipmentDetails(prevState => ({
            ...prevState,
            [name]: value,
        }));
    }

    const handleSourceDoc = e => {
        const file = e.target.files[0] || null;
        setEquipmentDetails(prevState => ({
            ...prevState,
            sourceDocument: file,
        }));
    }

    const onSubmit = async () => {
        if (!equipmentDetails.name.trim() || !equipmentDetails.type) {
            setFormError('Please provide an equipment name and type when submitting new Equipment')
            return
        }
        if (equipmentDetails.type === 'Other' && !equipmentDetails.typeOther.trim()) {
            setFormError('Please specify the equipment type')
            return
        }
        if (equipmentDetails.mass !== '' && !(Number(equipmentDetails.mass) >= 0)) {
            setFormError('Equipment mass must be a number of zero or more')
            return
        }
        if (equipmentDetails.mass !== '' && !equipmentDetails.massUnit) {
            setFormError('Please select the unit of the equipment mass')
            return
        }

        const formData = new FormData();
        for (let key in equipmentDetails) {
            if (key !== 'sourceDocument') {
                formData.append(key, equipmentDetails[key]);
            }
        }
        if (equipmentDetails.sourceDocument) {
            formData.append('sourceDocument', equipmentDetails.sourceDocument);
        }

        setSubmitting(true)
        const saved = await dispatch(postEquipment(formData, projectKey))
        setSubmitting(false)
        if (saved) {
            dispatch(getEquipment(projectKey))
            handleReset()
        }
    }

    const handleReset = () => {
        setEquipmentDetails({ ...initialState });
        setFormError('');
        if (fileInput.current) {
            fileInput.current.value = '';
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex' }}>
                <Typography variant='h4'>
                    Processing Equipment
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Equipment name
                </Typography>
                <TextField
                    name='name'
                    value={equipmentDetails.name}
                    label='Equipment name'
                    onChange={setData}
                    fullWidth
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Type
                </Typography>
                <FormControl
                    fullWidth
                >
                    <InputLabel>
                        Type
                    </InputLabel>
                    <Select name='type' label='Type' value={equipmentDetails.type} onChange={setData}>
                        {equipmentTypes.map(type => (
                            <MenuItem key={type} value={type}>
                                {type === 'Other' ? 'Other (specify)' : type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {equipmentDetails.type === 'Other' &&
                    <TextField
                        name='typeOther'
                        value={equipmentDetails.typeOther}
                        label='Please specify the equipment type'
                        onChange={setData}
                        fullWidth
                    />
                }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Manufacturer
                </Typography>
                <TextField
                    name='manufacturer'
                    value={equipmentDetails.manufacturer}
                    label='Manufacturer'
                    onChange={setData}
                    fullWidth
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Model
                </Typography>
                <TextField
                    name='model'
                    value={equipmentDetails.model}
                    label='Model'
                    onChange={setData}
                    fullWidth
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Serial number
                </Typography>
                <TextField
                    name='serialNumber'
                    value={equipmentDetails.serialNumber}
                    label='Serial number'
                    onChange={setData}
                    fullWidth
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    What is the unit of the equipment mass?
                </Typography>
                <FormControl
                    fullWidth
                >
                    <InputLabel>
                        Unit
                    </InputLabel>
                    <Select name='massUnit' label='Unit' value={equipmentDetails.massUnit} onChange={setData}>
                        {massUnits.map(unit => (
                            <MenuItem key={unit} value={unit}>
                                {unit}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='h6'>
                    Equipment mass
                </Typography>
                <TextField
                    name='mass'
                    type='number'
                    value={equipmentDetails.mass}
                    label='Equipment mass'
                    onChange={setData}
                    inputProps={{ min: 0, step: 'any' }}
                    fullWidth
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                    component='label'
                    variant='outlined'
                    startIcon={<FileUploadIcon />}
                    sx={{ width: 'fit-content' }}
                >
                    Upload photo of equipment or specification sheet
                    <input
                        ref={fileInput}
                        type='file'
                        hidden
                        accept='.pdf,.jpg,.jpeg,.png'
                        onChange={handleSourceDoc}
                    />
                </Button>
                {equipmentDetails.sourceDocument &&
                    <Typography variant='body2'>
                        {equipmentDetails.sourceDocument.name}
                    </Typography>
                }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formError &&
                    <Alert sx={{ width: 'fit-content' }} severity='error'>
                        {formError}
                    </Alert>
                }
                <Button
                    variant='contained'
                    onClick={onSubmit}
                    disabled={submitting}
                    sx={{ width: 'fit-content' }}
                >
                    Submit
                </Button>
            </Box>
            <Divider />
            <EquipmentList projectKey={projectKey} />
        </Box>
    );
}
